/**
 * WHAT EACH TOOL IS CALLED, in the words of somebody who is not a programmer.
 *
 * ── WHY THIS SHELF AND NOT A FEATURE DICTIONARY ───────────────────────────
 *
 * Two screens read a turn's tool calls and neither may import the other: the
 * agent panel (features/agents) and the conversation window (features/inbox),
 * which now draws the assistant's work INSIDE the bubble it produced. A feature
 * never imports another feature, so these sentences would have had to be written
 * twice — thirty-one of them in seven languages, drifting apart the first time
 * somebody renamed a tool. shared/ is the one shelf both layers take from, for
 * the same reason table-labels.ts and agent-roles.ts sit beside this file.
 *
 * ── WHY IT IS SPREAD INTO A DICTIONARY AND IS NOT A COMPOSABLE ────────────
 *
 * A component may hold exactly ONE local i18n scope; a second
 * useI18n({ useScope: "local" }) in the same file is ignored in silence and the
 * screen shows the wrong word. So this is not a composable — it is a block of
 * messages that a feature's own dictionary spreads into each of its locales, and
 * the feature's single `t` then resolves `tool.*` like any other key of its own.
 *
 * ── THE FALLBACK IS THE POINT ─────────────────────────────────────────────
 *
 * It is a VIEW over names the platform already publishes at /v3/agents/tools, so
 * it adds no capability and no coupling. A tool this dictionary has never heard
 * of falls back to its own name with the underscores taken out — worse than a
 * label, and far better than write_structure printed beside a customer's answer.
 *
 * features/agents/i18n/agents.ts spreads this block too (19/10/2026): its own copy
 * had drifted — "Rodando o artefato", and no word for the attendant's own hands
 * (reply, react, set_field…), which "Sempre sabe" printed as English codes.
 */
/**
 * As operações que só LEEM, por ferramenta: num diagnóstico só de leitura, o
 * rótulo da ferramenta inteira ("Escrevendo o site") mentia. Espelha o `op` de
 * cada ferramenta no servidor; uma op nova cai no rótulo de sempre (`tool.*`).
 */
const READ_OPS: Record<string, ReadonlySet<string>> = {
	automation: new Set(["list", "history", "pending", "execution", "waits"]),
	site: new Set(["list", "read", "inspect", "blocks", "revisions"]),
	files: new Set(["read", "list", "versions"]),
	embeddables: new Set(["list"]),
};

/**
 * Se o passo só lê. `input` é o JSON dos argumentos (passo gravado) ou o resumo
 * "op: list · path: …" (passo ao vivo) — os dois dizem o `op`.
 */
export function readsOnly(tool: string, input?: string): boolean {
	const op = input?.match(/(?:^|[{,·]\s*)"?op"?\s*:\s*"?(\w+)/)?.[1];
	return op !== undefined && READ_OPS[tool]?.has(op) === true;
}

export const agentToolMessages = {
	en: {
		tool: {
			query: "Querying the database",
			query_records: "Reading records",
			read_structure: "Looking at the screens and fields",
			read_system: "Reading the system",
			change_system: "Changing the system",
			files: "Working on the files",
			web: "Searching the web",
			site: "Writing the site",
			embeddables: "Wiring chat and forms",
			platform: "Checking the platform",
			memory: "Consulting the memory",
			write_structure: "Creating or changing a screen",
			write_record: "Saving a record",
			write_funnel: "Creating a funnel",
			place_on_funnel: "Placing on the funnel",
			move_card: "Moving between stages",
			write_artifact: "Writing a function",
			test_artifact: "Testing the function",
			run_artifact: "Running the function",
			create_contact: "Creating the contact",
			update_contact: "Updating the contact",
			navigate: "Opening a screen",
			send_message: "Sending a message",
			prepare_message: "Preparing a reply for you",
			http_request: "Calling an external system",
			schedule: "Scheduling",
			media: "Generating or transcribing media",
			request_human_help: "Calling a colleague",
			spawn_agent: "Delegating to another agent",
			write_business_profile: "Updating the business profile",
			blueprint: "Writing the proposal",
			library: "Reading the company's documents",
			manual: "Consulting the product manual",
			automation: "Creating or changing an automation",
			capture_logo: "Capturing the logo",
			reply: "Replying",
			react: "Reacting to a message",
			stay_silent: "Staying silent",
			set_field: "Filling in a field",
			conversation: "Looking after the conversation",
			transfer_conversation: "Handing over the conversation",
			form_link: "Sending a form link",
		},
		toolRead: {
			automation: "Looking at the automations",
			site: "Reading the site",
			files: "Looking at the files",
			embeddables: "Looking at chat and forms",
		},
	},
	"pt-BR": {
		tool: {
			query: "Consultando o banco",
			query_records: "Lendo registros",
			read_structure: "Vendo as telas e campos",
			read_system: "Lendo o sistema",
			change_system: "Mudando o sistema",
			files: "Mexendo nos arquivos",
			web: "Buscando na web",
			site: "Escrevendo o site",
			embeddables: "Ligando chat e formulários",
			platform: "Verificando a plataforma",
			memory: "Consultando a memória",
			write_structure: "Criando ou alterando uma tela",
			write_record: "Gravando um registro",
			write_funnel: "Criando um funil",
			place_on_funnel: "Colocando no funil",
			move_card: "Movendo de etapa",
			write_artifact: "Escrevendo uma função",
			test_artifact: "Testando a função",
			run_artifact: "Rodando a função",
			create_contact: "Criando o contato",
			update_contact: "Atualizando o contato",
			navigate: "Abrindo uma tela",
			send_message: "Enviando mensagem",
			prepare_message: "Preparando uma resposta para você",
			http_request: "Chamando um sistema externo",
			schedule: "Agendando",
			media: "Gerando ou transcrevendo mídia",
			request_human_help: "Chamando um colega",
			spawn_agent: "Delegando para outro agente",
			write_business_profile: "Atualizando a ficha do negócio",
			blueprint: "Escrevendo a proposta",
			library: "Lendo os documentos da empresa",
			manual: "Consultando o manual do produto",
			automation: "Criando ou alterando uma automação",
			capture_logo: "Capturando o logotipo",
			reply: "Respondendo",
			react: "Reagindo a uma mensagem",
			stay_silent: "Ficando em silêncio",
			set_field: "Preenchendo um campo",
			conversation: "Cuidando da conversa",
			transfer_conversation: "Transferindo o atendimento",
			form_link: "Enviando o link de um formulário",
		},
		toolRead: {
			automation: "Consultando as automações",
			site: "Lendo o site",
			files: "Lendo os arquivos",
			embeddables: "Vendo chat e formulários",
		},
	},
	el: {
		tool: {
			query: "Αναζήτηση στη βάση δεδομένων",
			query_records: "Ανάγνωση εγγραφών",
			read_structure: "Εξέταση οθονών και πεδίων",
			read_system: "Ανάγνωση του συστήματος",
			change_system: "Αλλαγή του συστήματος",
			files: "Επεξεργασία των αρχείων",
			web: "Αναζήτηση στο web",
			site: "Συγγραφή του ιστότοπου",
			embeddables: "Σύνδεση chat και φορμών",
			platform: "Έλεγχος της πλατφόρμας",
			memory: "Αναζήτηση στη μνήμη",
			write_structure: "Δημιουργία ή αλλαγή οθόνης",
			write_record: "Αποθήκευση εγγραφής",
			write_funnel: "Δημιουργία χοάνης",
			place_on_funnel: "Τοποθέτηση στη χοάνη",
			move_card: "Μετακίνηση μεταξύ σταδίων",
			write_artifact: "Συγγραφή συνάρτησης",
			test_artifact: "Δοκιμή της συνάρτησης",
			run_artifact: "Εκτέλεση της συνάρτησης",
			create_contact: "Δημιουργία επαφής",
			update_contact: "Ενημέρωση της επαφής",
			navigate: "Άνοιγμα οθόνης",
			send_message: "Αποστολή μηνύματος",
			prepare_message: "Ετοιμάζει μια απάντηση για εσάς",
			http_request: "Κλήση εξωτερικού συστήματος",
			schedule: "Προγραμματισμός",
			media: "Δημιουργία ή απομαγνητοφώνηση πολυμέσων",
			request_human_help: "Κλήση συναδέλφου",
			spawn_agent: "Ανάθεση σε άλλον πράκτορα",
			write_business_profile: "Ενημέρωση της ταυτότητας της επιχείρησης",
			blueprint: "Συγγραφή της πρότασης",
			library: "Ανάγνωση των εγγράφων της επιχείρησης",
			manual: "Αναζήτηση στο εγχειρίδιο του προϊόντος",
			automation: "Δημιουργία ή αλλαγή αυτοματισμού",
			capture_logo: "Λήψη του λογότυπου",
			reply: "Απάντηση",
			react: "Αντίδραση σε μήνυμα",
			stay_silent: "Παραμένει σιωπηλός",
			set_field: "Συμπλήρωση πεδίου",
			conversation: "Φροντίδα της συνομιλίας",
			transfer_conversation: "Μεταφορά της συνομιλίας",
			form_link: "Αποστολή συνδέσμου φόρμας",
		},
		toolRead: {
			automation: "Εξέταση των αυτοματισμών",
			site: "Ανάγνωση του ιστότοπου",
			files: "Ανάγνωση των αρχείων",
			embeddables: "Εξέταση chat και φορμών",
		},
	},
	es: {
		tool: {
			query: "Consultando la base de datos",
			query_records: "Leyendo registros",
			read_structure: "Mirando las pantallas y los campos",
			read_system: "Leyendo el sistema",
			change_system: "Cambiando el sistema",
			files: "Trabajando en los archivos",
			web: "Buscando en la web",
			site: "Escribiendo la web",
			embeddables: "Conectando chat y formularios",
			platform: "Comprobando la plataforma",
			memory: "Consultando la memoria",
			write_structure: "Creando o cambiando una pantalla",
			write_record: "Guardando un registro",
			write_funnel: "Creando un embudo",
			place_on_funnel: "Colocando en el embudo",
			move_card: "Moviendo entre etapas",
			write_artifact: "Escribiendo una función",
			test_artifact: "Probando la función",
			run_artifact: "Ejecutando la función",
			create_contact: "Creando el contacto",
			update_contact: "Actualizando el contacto",
			navigate: "Abriendo una pantalla",
			send_message: "Enviando un mensaje",
			prepare_message: "Preparando una respuesta para ti",
			http_request: "Llamando a un sistema externo",
			schedule: "Programando",
			media: "Generando o transcribiendo medios",
			request_human_help: "Llamando a un compañero",
			spawn_agent: "Delegando en otro agente",
			write_business_profile: "Actualizando la ficha del negocio",
			blueprint: "Escribiendo la propuesta",
			library: "Leyendo los documentos de la empresa",
			manual: "Consultando el manual del producto",
			automation: "Creando o cambiando una automatización",
			capture_logo: "Capturando el logotipo",
			reply: "Respondiendo",
			react: "Reaccionando a un mensaje",
			stay_silent: "Quedándose en silencio",
			set_field: "Rellenando un campo",
			conversation: "Atendiendo la conversación",
			transfer_conversation: "Transfiriendo la atención",
			form_link: "Enviando el enlace de un formulario",
		},
		toolRead: {
			automation: "Consultando las automatizaciones",
			site: "Leyendo el sitio",
			files: "Leyendo los archivos",
			embeddables: "Viendo el chat y los formularios",
		},
	},
	fr: {
		tool: {
			query: "Interrogation de la base de données",
			query_records: "Lecture des fiches",
			read_structure: "Examen des écrans et des champs",
			read_system: "Lecture du système",
			change_system: "Modification du système",
			files: "Travail sur les fichiers",
			web: "Recherche sur le web",
			site: "Rédaction du site",
			embeddables: "Branchement du chat et des formulaires",
			platform: "Vérification de la plateforme",
			memory: "Consultation de la mémoire",
			write_structure: "Création ou modification d'un écran",
			write_record: "Enregistrement d'une fiche",
			write_funnel: "Création d'un pipeline",
			place_on_funnel: "Placement sur le pipeline",
			move_card: "Déplacement entre étapes",
			write_artifact: "Rédaction d'une fonction",
			test_artifact: "Test de la fonction",
			run_artifact: "Exécution de la fonction",
			create_contact: "Création du contact",
			update_contact: "Mise à jour du contact",
			navigate: "Ouverture d'un écran",
			send_message: "Envoi d'un message",
			prepare_message: "Préparation d'une réponse pour vous",
			http_request: "Appel à un système externe",
			schedule: "Planification",
			media: "Génération ou transcription de médias",
			request_human_help: "Appel à un collègue",
			spawn_agent: "Délégation à un autre agent",
			write_business_profile: "Mise à jour de la fiche de l'entreprise",
			blueprint: "Rédaction de la proposition",
			library: "Lecture des documents de l'entreprise",
			manual: "Consultation du manuel du produit",
			automation: "Création ou modification d'une automatisation",
			capture_logo: "Capture du logo",
			reply: "Réponse",
			react: "Réaction à un message",
			stay_silent: "Reste silencieux",
			set_field: "Remplissage d'un champ",
			conversation: "Suivi de la conversation",
			transfer_conversation: "Transfert de la conversation",
			form_link: "Envoi du lien d'un formulaire",
		},
		toolRead: {
			automation: "Consultation des automatisations",
			site: "Lecture du site",
			files: "Lecture des fichiers",
			embeddables: "Consultation du chat et des formulaires",
		},
	},
	it: {
		tool: {
			query: "Interrogazione del database",
			query_records: "Lettura dei record",
			read_structure: "Esame di schermate e campi",
			read_system: "Lettura del sistema",
			change_system: "Modifica del sistema",
			files: "Lavoro sui file",
			web: "Ricerca sul web",
			site: "Scrittura del sito",
			embeddables: "Collegamento di chat e moduli",
			platform: "Controllo della piattaforma",
			memory: "Consultazione della memoria",
			write_structure: "Creazione o modifica di una schermata",
			write_record: "Salvataggio di un record",
			write_funnel: "Creazione di una pipeline",
			place_on_funnel: "Inserimento nella pipeline",
			move_card: "Spostamento tra fasi",
			write_artifact: "Scrittura di una funzione",
			test_artifact: "Prova della funzione",
			run_artifact: "Esecuzione della funzione",
			create_contact: "Creazione del contatto",
			update_contact: "Aggiornamento del contatto",
			navigate: "Apertura di una schermata",
			send_message: "Invio di un messaggio",
			prepare_message: "Preparazione di una risposta per te",
			http_request: "Chiamata a un sistema esterno",
			schedule: "Programmazione",
			media: "Generazione o trascrizione di media",
			request_human_help: "Chiamata a un collega",
			spawn_agent: "Delega a un altro agente",
			write_business_profile: "Aggiornamento dell'anagrafica aziendale",
			blueprint: "Scrittura della proposta",
			library: "Lettura dei documenti dell'azienda",
			manual: "Consultazione del manuale del prodotto",
			automation: "Creazione o modifica di un'automazione",
			capture_logo: "Acquisizione del logo",
			reply: "Risposta",
			react: "Reazione a un messaggio",
			stay_silent: "Resta in silenzio",
			set_field: "Compilazione di un campo",
			conversation: "Cura della conversazione",
			transfer_conversation: "Trasferimento della conversazione",
			form_link: "Invio del link di un modulo",
		},
		toolRead: {
			automation: "Consultazione delle automazioni",
			site: "Lettura del sito",
			files: "Lettura dei file",
			embeddables: "Consultazione di chat e moduli",
		},
	},
	de: {
		tool: {
			query: "Datenbank wird abgefragt",
			query_records: "Datensätze werden gelesen",
			read_structure: "Masken und Felder werden angesehen",
			read_system: "Das System wird gelesen",
			change_system: "Das System wird geändert",
			files: "An den Dateien wird gearbeitet",
			web: "Im Web wird gesucht",
			site: "Die Website wird geschrieben",
			embeddables: "Chat und Formulare werden verdrahtet",
			platform: "Die Plattform wird geprüft",
			memory: "Das Gedächtnis wird abgefragt",
			write_structure: "Eine Maske wird angelegt oder geändert",
			write_record: "Ein Datensatz wird gespeichert",
			write_funnel: "Eine Pipeline wird angelegt",
			place_on_funnel: "Wird in die Pipeline gelegt",
			move_card: "Wird zwischen Phasen verschoben",
			write_artifact: "Eine Funktion wird geschrieben",
			test_artifact: "Die Funktion wird getestet",
			run_artifact: "Die Funktion wird ausgeführt",
			create_contact: "Kontakt wird erstellt",
			update_contact: "Der Kontakt wird aktualisiert",
			navigate: "Eine Maske wird geöffnet",
			send_message: "Eine Nachricht wird gesendet",
			prepare_message: "Eine Antwort wird für Sie vorbereitet",
			http_request: "Ein externes System wird aufgerufen",
			schedule: "Wird geplant",
			media: "Medien werden erzeugt oder transkribiert",
			request_human_help: "Ein Kollege wird geholt",
			spawn_agent: "Wird an einen anderen Agenten delegiert",
			write_business_profile: "Das Unternehmensprofil wird aktualisiert",
			blueprint: "Der Vorschlag wird geschrieben",
			library: "Die Unternehmensdokumente werden gelesen",
			manual: "Das Produkthandbuch wird gelesen",
			automation: "Eine Automatisierung wird angelegt oder geändert",
			capture_logo: "Das Logo wird erfasst",
			reply: "Antwortet",
			react: "Reagiert auf eine Nachricht",
			stay_silent: "Bleibt still",
			set_field: "Ein Feld wird ausgefüllt",
			conversation: "Kümmert sich um das Gespräch",
			transfer_conversation: "Übergibt das Gespräch",
			form_link: "Sendet einen Formularlink",
		},
		toolRead: {
			automation: "Die Automatisierungen werden angesehen",
			site: "Die Website wird gelesen",
			files: "Die Dateien werden gelesen",
			embeddables: "Chat und Formulare werden angesehen",
		},
	},
};
